import {
	LoginRequestDto,
	LogoutRequestDto,
	LoginSSORequestDto,
} from "@api-types/user/LoginRequestDto";
import {
	LoginResponseDto,
	LoginResponseUser,
	LoginUserMatchingResponseDto,
	RedirectLoginResponseDto,
} from "@api-types/user/LoginResponseDto";
import { AccountActivationRequestDto } from "@api-types/user/AccountActivationRequestDto";
import { ForcePasswordChangeRequestDto } from "@api-types/user/ForcePasswordChangeRequestDto";
import { UserMatchingRequestDto } from "@api-types/user/UserMatchingRequestDto";
import { getWebApiUrl, httpClient, mapObjectToUrlEncoded } from "@utils";
import { MitAuthDto } from "@api-types/integrations/MitAuthDto";
import { BankIdAuthDto } from "@api-types/integrations";
import CaptchaService from "@services/captcha/CaptchaService";
import { ApiRequestContextData, ContextOptions } from "@utils/api-config/types";
import { getMvcRequestHeaders } from "@utils/api-config/getMvcRequestHeaders";
import { getApiRequestContext } from "@utils/api-config";

export default class UserApiService {
	private static get baseUrl(): string {
		return `${getWebApiUrl()}/api/user`;
	}

	static getRequestHeaders = (
		context: ApiRequestContextData | null
	): HeadersInit => {
		const appHeaders = getMvcRequestHeaders(context);
		appHeaders["Content-Type" as keyof HeadersInit] = "application/json";
		return appHeaders;
	};

	static async loginUser(
		userData: LoginRequestDto,
		options?: ContextOptions
	): Promise<LoginResponseDto | RedirectLoginResponseDto> {
		const context = getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);
		headers["Content-Type" as keyof HeadersInit] =
			"application/x-www-form-urlencoded";
		const url = `${this.baseUrl}/login`;

		const token = await CaptchaService.executeCaptcha("login");

		if (token) {
			userData.captchaResponse = token;
		}

		const body = mapObjectToUrlEncoded(userData);

		return httpClient.post(url, body, headers);
	}

	static async loginUserWithMit(
		mitAuthDto: MitAuthDto,
		options?: ContextOptions
	): Promise<LoginResponseDto> {
		const context = getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);
		const url = `${this.baseUrl}/login/mit`;
		return httpClient.post(url, JSON.stringify(mitAuthDto), headers);
	}

	static async loginUserWithBankId(
		bankIdAuthData: BankIdAuthDto,
		options?: ContextOptions
	): Promise<LoginResponseDto> {
		const context = getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);
		const url = `${this.baseUrl}/login/bankid`;
		return httpClient.post(url, JSON.stringify(bankIdAuthData), headers);
	}

	static async confirmUserMatching(
		userData: UserMatchingRequestDto,
		options?: ContextOptions
	): Promise<LoginUserMatchingResponseDto> {
		const url = `${this.baseUrl}/confirm-user-matching`;
		const context = getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);
		return httpClient.post(url, JSON.stringify(userData), headers);
	}

	static async ssoLogin(
		data: LoginSSORequestDto,
		options?: ContextOptions
	): Promise<LoginResponseDto> {
		const url = `${this.baseUrl}/sso`;
		const context = getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);
		const response = httpClient.get(`${url}?token=${data.token}`, headers);
		return response;
	}

	static async gamesSsoLogin(
		token: string,
		options?: ContextOptions
	): Promise<LoginResponseDto> {
		const url = `${this.baseUrl}/login/games-sso`;
		const context = getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);
		const response = await httpClient.get(`${url}?token=${token}`, headers);
		return response;
	}

	static async logoutUser(
		requestModel: LogoutRequestDto,
		options?: ContextOptions
	): Promise<void> {
		const url = `${this.baseUrl}/logout`;
		const context = getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);
		await httpClient.post(url, JSON.stringify(requestModel), headers, {
			responseType: "raw",
		});
	}

	static async accountActivation(
		requestModel: AccountActivationRequestDto,
		options?: ContextOptions
	): Promise<LoginResponseDto> {
		const url = `${this.baseUrl}/account-activation`;
		const context = getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);
		return httpClient.post(url, JSON.stringify(requestModel), headers);
	}

	static async forcePasswordChange(
		requestModel: ForcePasswordChangeRequestDto,
		options?: ContextOptions
	): Promise<LoginResponseDto> {
		const url = `${this.baseUrl}/force-password-change`;
		const context = getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);
		return httpClient.post(url, JSON.stringify(requestModel), headers);
	}

	static async acceptGDPRConsent(options?: ContextOptions): Promise<boolean> {
		const url = `${this.baseUrl}/accept-gdpr`;
		const context = getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);
		return httpClient.get(url, headers);
	}

	static async refreshUser(
		options: ContextOptions
	): Promise<LoginResponseUser> {
		const context = getApiRequestContext(options);
		const url = `${this.baseUrl}/refresh-user?userId=${context?.userId}`;
		const headers = this.getRequestHeaders(context);
		return httpClient.get(url, headers);
	}
}
